// OverflowTooltip — a truncation-recovery tooltip, shown ONLY when the label
// is actually clipped.
//
// Reuses the Radix-backed Tooltip primitives for all the a11y — `role=
// "tooltip"`, `aria-describedby` on the trigger, opens on hover AND keyboard
// focus, Esc-dismissible, portalled + collision-aware placement — and adds the
// one thing they lack: the OVERFLOW GATE (`useIsTruncated`). A label that fits
// its box gets no tooltip, because there is nothing to recover; a clipped one
// reveals its full text. This is the honest shape a bare native `title` cannot
// be — `title` is always-on (even when the text fits) and keyboard-unreachable.
//
// The child is a RENDER-PROP that receives the `measureRef` to place on the
// clipping element (the label span). The node the render-prop returns is the
// Radix trigger (`asChild`), so it MUST be a single focusable element (the row
// / tab button) — hover and focus on the whole control drive the tip while the
// ref measures the inner text. Reusable anywhere a label clips (rail rows now,
// tab titles later).
//
// Pure-ish leaf: local open/measurement state only, no ports, no I/O beyond the
// layout read in `useIsTruncated`. Requires a `TooltipProvider` ancestor (as
// every Radix tooltip does) — the consuming surface mounts one.

import * as RadixTooltip from "@radix-ui/react-tooltip";
import type { ReactNode } from "react";
import { useState } from "react";
import type { TooltipSide } from "./Tooltip.js";
import "./Tooltip.css";
import { useIsTruncated } from "./useIsTruncated.js";

// The gap (px) between the trigger and the tip — mirrors the base Tooltip.
const SIDE_OFFSET = 6;

export type OverflowTooltipProps = {
    // The full text to reveal when clipped (typically the same string the
    // label renders). Kept a ReactNode so a caller can pass richer content.
    readonly content: ReactNode;
    // Placement; defaults to "right" (a left-rail row reveals its full name
    // into the canvas). Radix flips it on collision.
    readonly side?: TooltipSide;
    // Hover dwell before showing; focus shows immediately (Radix). Short by
    // default so a deliberate hover reveals quickly without pass-through flicker.
    readonly delayDuration?: number;
    // Renders the trigger; MUST return a single focusable element and place
    // `measureRef` on the clipping label inside it.
    readonly children: (
        measureRef: (node: HTMLElement | null) => void,
    ) => ReactNode;
};

export const OverflowTooltip = ({
    content,
    side = "right",
    delayDuration = 300,
    children,
}: OverflowTooltipProps) => {
    const { ref, truncated } = useIsTruncated();
    const [wantsOpen, setWantsOpen] = useState(false);
    return (
        <RadixTooltip.Root
            // The overflow gate: the tip can open only while the label is
            // actually clipped. A label that fits never opens, whatever the
            // pointer/focus does.
            open={truncated && wantsOpen}
            onOpenChange={setWantsOpen}
            delayDuration={delayDuration}
        >
            <RadixTooltip.Trigger asChild>{children(ref)}</RadixTooltip.Trigger>
            <RadixTooltip.Portal>
                <RadixTooltip.Content
                    className="bilda-tooltip"
                    side={side}
                    sideOffset={SIDE_OFFSET}
                >
                    {content}
                    <RadixTooltip.Arrow className="bilda-tooltip__arrow" />
                </RadixTooltip.Content>
            </RadixTooltip.Portal>
        </RadixTooltip.Root>
    );
};
